/**
 * Admin Web Push — fully isolated from merchant users.
 * - Separate service worker (scope /admin/, admin-sw.js)
 * - Separate VAPID keys (VAPID_ADMIN_* on server)
 * - Separate backend table (admin_push_subscriptions)
 */

import adminHttp from '../api/admin-http';
import {
  getNotificationPermission,
  isPushSupported,
  requestNotificationPermission,
} from '../utils/notifications';

const ADMIN_SW_URL = '/admin-sw.js';
const ADMIN_SW_SCOPE = '/admin/';

function parseClientDeviceName(): string {
  const ua = navigator.userAgent;
  let platform = 'Unknown';
  if (/Windows/i.test(ua)) platform = 'Windows';
  else if (/Macintosh|Mac OS/i.test(ua)) platform = 'macOS';
  else if (/Android/i.test(ua)) platform = 'Android';
  else if (/iPhone|iPad|iPod/i.test(ua)) platform = 'iOS';
  else if (/Linux/i.test(ua)) platform = 'Linux';

  let browser = 'Browser';
  if (/Edg\//i.test(ua)) browser = 'Edge';
  else if (/Chrome\//i.test(ua)) browser = 'Chrome';
  else if (/Firefox\//i.test(ua)) browser = 'Firefox';
  else if (/Safari\//i.test(ua)) browser = 'Safari';

  return `${browser} on ${platform}`;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/** Admin-scoped SW — separate push subscription from merchant PWA at scope /. */
export async function waitForAdminServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers are not supported');
  }

  let registration = await navigator.serviceWorker.getRegistration(ADMIN_SW_SCOPE);
  if (!registration) {
    registration = await navigator.serviceWorker.register(ADMIN_SW_URL, { scope: ADMIN_SW_SCOPE });
  }

  const worker = registration.installing || registration.waiting || registration.active;
  if (worker && worker.state !== 'activated') {
    await new Promise<void>((resolve) => {
      const onStateChange = () => {
        if (worker.state === 'activated') {
          worker.removeEventListener('statechange', onStateChange);
          resolve();
        }
      };
      worker.addEventListener('statechange', onStateChange);
      if (worker.state === 'activated') {
        worker.removeEventListener('statechange', onStateChange);
        resolve();
      }
    });
  }

  return registration;
}

export async function fetchAdminVapidPublicKey(): Promise<string | null> {
  try {
    const res = await adminHttp.get('/admin/push/vapid-public');
    return res.data?.vapid_public_key ?? null;
  } catch {
    return null;
  }
}

export async function saveAdminPushSubscriptionToBackend(sub: PushSubscriptionJSON): Promise<boolean> {
  if (!sub.endpoint || !sub.keys?.p256dh || !sub.keys?.auth) return false;

  await adminHttp.post('/admin/push-subscription', {
    endpoint: sub.endpoint,
    keys: { p256dh: sub.keys.p256dh, auth: sub.keys.auth },
    user_agent: navigator.userAgent,
    device_name: parseClientDeviceName(),
  });

  await sendAdminPushHeartbeat(sub.endpoint).catch(() => {});
  return true;
}

export async function sendAdminPushHeartbeat(endpoint: string): Promise<void> {
  await adminHttp.post('/admin/push-subscription/heartbeat', { endpoint });
}

function applicationServerKeyMatches(subscription: PushSubscription, expectedKey: Uint8Array): boolean {
  const existing = subscription.options?.applicationServerKey;
  if (!existing) return false;
  const existingArr = existing instanceof ArrayBuffer ? new Uint8Array(existing) : new Uint8Array(existing as ArrayBuffer);
  if (existingArr.length !== expectedKey.length) return false;
  for (let i = 0; i < expectedKey.length; i++) {
    if (existingArr[i] !== expectedKey[i]) return false;
  }
  return true;
}

export async function registerAdminWebPushSubscription(): Promise<boolean> {
  if (!isPushSupported() || getNotificationPermission() !== 'granted') return false;
  if (!localStorage.getItem('admin_token')) return false;

  try {
    const registration = await waitForAdminServiceWorkerRegistration();
    const vapidPublicKey = await fetchAdminVapidPublicKey();
    if (!vapidPublicKey) return false;

    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey) as BufferSource;
    let subscription = await registration.pushManager.getSubscription();

    if (subscription && !applicationServerKeyMatches(subscription, applicationServerKey as Uint8Array)) {
      await subscription.unsubscribe();
      subscription = null;
    }

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    }

    return await saveAdminPushSubscriptionToBackend(subscription.toJSON());
  } catch (err) {
    console.warn('[AdminWebPush] Subscription failed:', err);
    return false;
  }
}

/** Re-sync admin subscription if admin token exists (works after page reload). */
export async function syncAdminPushIfAuthenticated(): Promise<boolean> {
  if (!localStorage.getItem('admin_token')) return false;
  if (getNotificationPermission() !== 'granted') return false;
  return registerAdminWebPushSubscription();
}

export async function hasActiveAdminPushSubscription(): Promise<boolean> {
  if (!isPushSupported() || !localStorage.getItem('admin_token')) return false;
  try {
    const registration = await waitForAdminServiceWorkerRegistration();
    return (await registration.pushManager.getSubscription()) !== null;
  } catch {
    return false;
  }
}

export interface AdminPushDevice {
  id: number;
  device_name: string;
  browser: string;
  platform: string;
  user_agent: string;
  last_seen_at: string | null;
  created_at: string;
}

export async function fetchAdminPushDevices(): Promise<AdminPushDevice[]> {
  const res = await adminHttp.get('/admin/push-subscription/devices');
  return res.data?.data ?? [];
}

export async function removeAdminPushDevice(id: number): Promise<void> {
  await adminHttp.delete(`/admin/push-subscription/devices/${id}`);
}

export async function requestAdminPushPermissionAndSubscribe(): Promise<boolean> {
  if (!isPushSupported()) return false;

  const permission = await requestNotificationPermission();
  if (permission !== 'granted') return false;

  return registerAdminWebPushSubscription();
}

/** Foreground fallback via admin-scoped service worker only. */
export async function showAdminForegroundNotification(
  title: string,
  options?: { body?: string; tag?: string; url?: string; type?: string }
): Promise<void> {
  if (!isPushSupported() || getNotificationPermission() !== 'granted') return;

  try {
    const registration = await waitForAdminServiceWorkerRegistration();
    const worker = registration.active;
    if (worker) {
      worker.postMessage({
        type: 'SHOW_ADMIN_NOTIFICATION',
        payload: {
          title,
          body: options?.body,
          message: options?.body,
          tag: options?.tag,
          url: options?.url || '/admin',
          type: options?.type || 'admin_alert',
          audience: 'admin',
        },
      });
    }
  } catch {
    // ignore
  }
}
